var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, createContext, useContext, useCallback, useMemo, } from "react";
import { COMPONENT_OUTLINE_BORDER } from "../theme/componentBorders";
import { Card, Tabs, Space, Button, Tooltip, Select } from "antd";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
// 创建历史记录上下文
var HistoryContext = createContext(null);
// 导出 hook，供子组件使用
export var useContainerPanelHistory = function () {
    var context = useContext(HistoryContext);
    if (!context) {
        throw new Error("useContainerPanelHistory must be used within a ContainerPanel with showHistory enabled");
    }
    return context;
};
/**
 * 通用面板容器组件
 * 支持标题或标签页，支持右侧按钮分组，支持边框和阴影
 */
var ContainerPanel = function (_a) {
    var _b, _c, _d, _e;
    var title = _a.title, tabs = _a.tabs, activeTabKey = _a.activeTabKey, onTabChange = _a.onTabChange, _f = _a.buttons, buttons = _f === void 0 ? [] : _f, extraLeading = _a.extraLeading, children = _a.children, height = _a.height, _g = _a.contentPadding, contentPadding = _g === void 0 ? 16 : _g, _h = _a.bordered, bordered = _h === void 0 ? true : _h, _j = _a.shadow, shadow = _j === void 0 ? false : _j, _k = _a.cardProps, cardProps = _k === void 0 ? {} : _k, headerPadding = _a.headerPadding, _l = _a.collapsible, collapsible = _l === void 0 ? false : _l, _m = _a.defaultCollapsed, defaultCollapsed = _m === void 0 ? false : _m, onCollapseChange = _a.onCollapseChange, onTitleClick = _a.onTitleClick, _o = _a.showHistory, showHistory = _o === void 0 ? false : _o, onHistorySelect = _a.onHistorySelect, _p = _a.maxHistoryCount, maxHistoryCount = _p === void 0 ? 50 : _p;
    // 收拢状态
    var _q = useState(defaultCollapsed), collapsed = _q[0], setCollapsed = _q[1];
    // 历史记录状态
    var _r = useState([]), historyList = _r[0], setHistoryList = _r[1];
    // 切换收拢状态
    var handleToggleCollapse = function () {
        var newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        onCollapseChange === null || onCollapseChange === void 0 ? void 0 : onCollapseChange(newCollapsed);
    };
    // 保存历史记录
    var saveHistory = useCallback(function (data) {
        if (!showHistory)
            return;
        try {
            // 将数据转换为 JSON 字符串进行存储
            var jsonData_1 = typeof data === "string" ? data : JSON.stringify(data);
            setHistoryList(function (prev) {
                // 避免重复添加相同的历史记录
                var newList = __spreadArray([
                    jsonData_1
                ], prev.filter(function (item) { return item !== jsonData_1; }), true);
                // 限制历史记录数量
                return newList.slice(0, maxHistoryCount);
            });
        }
        catch (error) {
            console.error("保存历史记录失败:", error);
        }
    }, [showHistory, maxHistoryCount]);
    // 选择历史记录
    var handleHistorySelect = function (value) {
        try {
            // 尝试解析 JSON，如果失败则直接使用字符串
            var parsedData = void 0;
            try {
                parsedData = JSON.parse(value);
            }
            catch (_a) {
                parsedData = value;
            }
            onHistorySelect === null || onHistorySelect === void 0 ? void 0 : onHistorySelect(parsedData);
        }
        catch (error) {
            console.error("解析历史记录失败:", error);
        }
    };
    // 历史记录上下文值
    var historyContextValue = useMemo(function () { return ({
        saveHistory: saveHistory,
    }); }, [saveHistory]);
    // 渲染按钮组
    var renderButtonGroup = function (groupType) {
        // 过滤出该分组的按钮
        var groupButtons = buttons.filter(function (button) {
            return typeof button === "object" &&
                "group" in button &&
                button.group === groupType;
        });
        if (groupButtons.length === 0)
            return null;
        return (_jsx(Space, { children: groupButtons.map(function (button) {
                if (typeof button === "object" &&
                    "hidden" in button &&
                    button.hidden) {
                    return null;
                }
                if (typeof button === "object" && "key" in button) {
                    var buttonType = button.type === "danger" ? "default" : button.type;
                    var buttonContent = (_jsx(Button, __assign({ type: buttonType, danger: button.type === "danger" || button.danger, icon: button.icon, disabled: button.disabled, loading: button.loading, onClick: button.onClick, size: "small", style: button.style }, { children: button.showTextOnHover ? null : button.text })));
                    // 如果设置了 showTextOnHover 或 title，使用 Tooltip 包裹
                    if (button.showTextOnHover || button.title) {
                        var tooltipTitle = button.title || button.text || "";
                        return (_jsx(Tooltip, __assign({ title: tooltipTitle }, { children: buttonContent }), button.key));
                    }
                    return (_jsx(React.Fragment, { children: buttonContent }, button.key));
                }
                return null;
            }) }));
    };
    // 渲染历史记录下拉框
    var renderHistorySelect = function () {
        if (!showHistory || historyList.length === 0) {
            return null;
        }
        return (_jsx(Select, { placeholder: "\u5386\u53F2\u8BB0\u5F55", style: { width: 150, marginRight: 8 }, size: "small", value: undefined, onChange: handleHistorySelect, options: historyList.map(function (item, index) {
                // 尝试解析 JSON 以生成更友好的显示文本
                var displayText;
                try {
                    var parsed = JSON.parse(item);
                    // 如果解析成功，尝试生成简短描述
                    if (typeof parsed === "object" && parsed !== null) {
                        displayText = "\u8BB0\u5F55 ".concat(index + 1);
                    }
                    else {
                        displayText = String(parsed).substring(0, 20);
                    }
                }
                catch (_a) {
                    displayText = String(item).substring(0, 20);
                }
                return {
                    label: displayText,
                    value: item,
                };
            }) }));
    };
    // 渲染所有按钮
    var renderButtons = function () {
        // 分离 ButtonConfig 和 ReactNode
        var buttonConfigs = [];
        var reactNodes = [];
        // 如果有传入的按钮，处理它们
        if (Array.isArray(buttons) && buttons.length > 0) {
            buttons.forEach(function (btn) {
                if (React.isValidElement(btn)) {
                    reactNodes.push(btn);
                }
                else if (typeof btn === "object" && btn !== null) {
                    buttonConfigs.push(btn);
                }
            });
        }
        // 如果有收拢功能，将收拢按钮添加到 secondary 组（第二组，在 primary 组左侧）
        if (collapsible) {
            buttonConfigs.push({
                key: "collapse",
                icon: collapsed ? _jsx(DownOutlined, {}) : _jsx(UpOutlined, {}),
                type: "text",
                group: "secondary",
                onClick: handleToggleCollapse,
                title: collapsed ? "展开" : "收拢",
                showTextOnHover: true,
                style: {
                    padding: 0,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            });
        }
        // 如果没有按钮且没有收拢功能，返回 null
        if (buttonConfigs.length === 0 && reactNodes.length === 0 && !collapsible) {
            return null;
        }
        // 如果只有 React 节点，直接渲染
        if (buttonConfigs.length === 0 && reactNodes.length > 0) {
            return (_jsx("div", __assign({ style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    justifyContent: "flex-end",
                } }, { children: _jsx(Space, { children: reactNodes }) })));
        }
        // 处理按钮配置
        var primaryButtons = buttonConfigs.filter(function (btn) { return "group" in btn && btn.group === "primary"; });
        var secondaryButtons = buttonConfigs.filter(function (btn) { return "group" in btn && btn.group === "secondary"; });
        var tertiaryButtons = buttonConfigs.filter(function (btn) { return "group" in btn && btn.group === "tertiary"; });
        var ungroupedButtons = buttonConfigs.filter(function (btn) { return !("group" in btn) || !btn.group; });
        // 判断是否有左侧按钮（tertiary）
        var hasTertiaryButtons = tertiaryButtons.length > 0;
        // 判断是否有右侧按钮组（primary 或 secondary）
        var hasRightButtonGroups = primaryButtons.length > 0 || secondaryButtons.length > 0;
        // 判断是否需要靠右对齐：如果没有左侧按钮，只有右侧按钮或未分组按钮，则整体靠右
        var shouldAlignRight = !hasTertiaryButtons &&
            (hasRightButtonGroups ||
                ungroupedButtons.length > 0 ||
                reactNodes.length > 0);
        return (_jsxs("div", __assign({ style: {
                display: "flex",
                alignItems: "center",
                gap: "16px",
                justifyContent: shouldAlignRight ? "flex-end" : "flex-start",
            } }, { children: [extraLeading, renderHistorySelect(), tertiaryButtons.length > 0 && renderButtonGroup("tertiary"), (primaryButtons.length > 0 || secondaryButtons.length > 0) && (_jsxs("div", __assign({ style: {
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        marginLeft: hasTertiaryButtons ? "auto" : undefined,
                    } }, { children: [secondaryButtons.length > 0 && renderButtonGroup("secondary"), primaryButtons.length > 0 && renderButtonGroup("primary")] }))), ungroupedButtons.length > 0 && (_jsx(Space, { children: ungroupedButtons.map(function (button) {
                        if (typeof button === "object" &&
                            "hidden" in button &&
                            button.hidden) {
                            return null;
                        }
                        if (typeof button === "object" && "key" in button) {
                            var buttonType = button.type === "danger" ? "default" : button.type;
                            var buttonContent = (_jsx(Button, __assign({ type: buttonType, danger: button.type === "danger", icon: button.icon, disabled: button.disabled, onClick: button.onClick, size: "small", style: button.style }, { children: button.showTextOnHover ? null : button.text })));
                            // 如果设置了 showTextOnHover 或 title，使用 Tooltip 包裹
                            if (button.showTextOnHover || button.title) {
                                var tooltipTitle = button.title || button.text || "";
                                return (_jsx(Tooltip, __assign({ title: tooltipTitle }, { children: buttonContent }), button.key));
                            }
                            return (_jsx(React.Fragment, { children: buttonContent }, button.key));
                        }
                        return null;
                    }) })), reactNodes.length > 0 && _jsx(Space, { children: reactNodes })] })));
    };
    // 处理标题点击事件
    var renderTitle = function () {
        if (title && onTitleClick) {
            var handleKeyDown = function (e) {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTitleClick();
                }
            };
            return (_jsx("button", __assign({ type: "button", onClick: onTitleClick, onKeyDown: handleKeyDown, style: {
                    cursor: "pointer",
                    userSelect: "none",
                    background: "none",
                    border: "none",
                    padding: 0,
                    margin: 0,
                    font: "inherit",
                    color: "inherit",
                    textAlign: "inherit",
                } }, { children: title })));
        }
        return title;
    };
    // 构建 Card 标题：tabs 模式下仅渲染标签栏，不包含内容
    var tabBarOnlyItems = tabs === null || tabs === void 0 ? void 0 : tabs.map(function (item) { return ({
        key: item.key,
        label: item.label,
        children: null,
    }); });
    var cardTitle = tabs ? (_jsx(Tabs, { className: "container-panel-tabs-header-only", items: tabBarOnlyItems, activeKey: activeTabKey, onChange: onTabChange })) : (renderTitle());
    // tabs 模式下，内容区渲染当前选中 tab 的 children；若 tab 无 children 则使用传入的 children（支持内容作为子节点传入）
    var bodyContent = tabs
        ? ((_c = (_b = tabs.find(function (t) { return t.key === activeTabKey; })) === null || _b === void 0 ? void 0 : _b.children) !== null && _c !== void 0 ? _c : children)
        : children;
    var cardHeight;
    if (height === undefined) {
        cardHeight = "100%";
    }
    else if (height === "auto") {
        cardHeight = "auto";
    }
    else if (typeof height === "number") {
        cardHeight = "".concat(height, "px");
    }
    else {
        cardHeight = height;
    }
    // 构建 Card 样式
    var cardStyle = __assign(__assign(__assign({ display: "flex", flexDirection: "column", border: bordered ? COMPONENT_OUTLINE_BORDER : "none" }, (shadow && {
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    })), cardProps.style), { height: cardHeight });
    // 构建 Card body 样式：height="auto" 时随内容自适应；有固定 height 时限制 body 不溢出；否则 flex 填满
    var isAutoHeight = height === "auto";
    var bodyStyle = __assign(__assign(__assign({ padding: typeof contentPadding === "number"
            ? "".concat(contentPadding, "px")
            : contentPadding }, (isAutoHeight
        ? { height: "auto", minHeight: "auto" }
        : { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" })), (height !== undefined && height !== "auto" && { overflow: "hidden" })), (_d = cardProps.styles) === null || _d === void 0 ? void 0 : _d.body);
    // 构建 Card header 样式（标题栏下划线最后设置，避免被 cardProps 覆盖）
    var headerStyle = __assign(__assign(__assign({}, (headerPadding && { padding: headerPadding })), (_e = cardProps.styles) === null || _e === void 0 ? void 0 : _e.header), { borderBottom: COMPONENT_OUTLINE_BORDER });
    return (_jsxs(_Fragment, { children: [tabs && (_jsx("style", { children: "\n            /* \u6807\u9898\u680F\u4EC5\u663E\u793A tab \u680F\uFF1A\u9690\u85CF tab \u5185\u5BB9\u533A\u57DF */\n            .container-panel-tabs-header-only .ant-tabs-content,\n            .container-panel-tabs-header-only .ant-tabs-content-holder {\n              display: none !important;\n            }\n            /* \u4EC5\u5728\u672C\u6807\u9898\u680F tab \u680F\u9690\u85CF\u5E95\u90E8\u5206\u9694\u7EBF\uFF0C\u907F\u514D\u5F71\u54CD\u5176\u4ED6\u9875\u7684 Tabs */\n            .container-panel-tabs-header-only .ant-tabs-nav::before {\n              display: none !important;\n            }\n          " })), _jsx(HistoryContext.Provider, __assign({ value: historyContextValue }, { children: _jsx(Card, __assign({}, cardProps, { title: cardTitle, extra: renderButtons(), style: cardStyle, styles: __assign(__assign({}, cardProps.styles), { body: bodyStyle, header: headerStyle }) }, { children: !collapsed && bodyContent })) }))] }));
};
export default ContainerPanel;
